/* eslint-disable @typescript-eslint/no-unused-vars */
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import HistoryIcon from "@mui/icons-material/History";
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Approver } from "../../../@types/DRO/GetAllApproverResponse";
import { GetAllVendorsListApiResponse } from "../../../@types/DRO/GetAllVendorsResponse";
import { GetAllWarehouseApiResponse } from "../../../@types/DRO/GetAllWarehouseResponse";
import {
  GetAllModelTypeArray,
  TransferOrderPartDetails,
} from "../../../@types/DRO/GetPartsListResponse";
import { LabelOption } from "../../../@types/DRO/LabelsResponse";
import { PurchaseOrderBody } from "../../../@types/DTO/CreatePurchaseOrderBody";
import { GetAllApproverListApi } from "../../../api/GetApproversList/GetAllApproverListApi";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { GetAllVendorsListApi } from "../../../api/GetVendorsList/GetAllVendorsListApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import { LabelsApi } from "../../../api/Labels/LabelsApi";
import { ProductOrderApi } from "../../../api/ProductOrder/ProductOrderApi";
import { isRoleDenied } from "../../../utils/permissions";
import { useLoading } from "../../../views/Loader/LoadingContext";
import {
  AppAutoCompleteWithSearchAPI,
  OptionType,
} from "../../Layouts/AppAutoCompleteWithSearchAPI/AppAutoCompleteWithSearchAPI";
import RoleGuard from "../../auth/RoleGuard";
import { PurchaseOrderHistory } from "../OrderHistory/PurchaseOrderHistory";

interface CreatePurchaseOrderProps {
  onBack: () => void;
  orderData?: any | null;
  isEditMode?: boolean;
  statusFilter?: string;
}

interface Item {
  description: string;
  referenceCode: string;
  quantity: string;
  unit: string;
  price: string;
  taxes: string;
  total: string;
  hsn: string;
  partId?: number;
  oemTypeId?: number; // Add this to track which OEM type the part belongs to
}

const CreatePurchaseOrder: React.FC<CreatePurchaseOrderProps> = ({
  onBack,
  orderData,
  isEditMode = false,
  statusFilter = "",
}) => {
  const { setLoading } = useLoading();
  const [items, setItems] = useState<
    (TransferOrderPartDetails & { hsn?: string; oemTypeId?: number })[]
  >([
    {
      id: -1,
      externalId: 0,
      referenceCode: "",
      type: "",
      description: "",
      status: 0,
      costPrice: 0,
      averageCostPrice: 0,
      salesPrice: 0,
      tax: 0,
      price: 0,
      quantity: 0,
      penaltyType: "",
      deleted: false,
      updatedBy: 0,
      hsn: undefined,
      oemTypeId: undefined,
    },
  ]);

  const [vendors, setVendors] = useState<
    GetAllVendorsListApiResponse["data"]["data"]
  >([]);
  const [warehouses, setWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);
  const [selectedVendor, setSelectedVendor] = useState<string>("");
  const [selectedGSTTreatment, setSelectedGSTTreatment] = useState<string>("");
  const [selectedWarehouses, setSelectedWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);
  const [partsCurrentPage, setPartsCurrentPage] = useState(0);
  const [vendorsCurrentPage, setVendorsCurrentPage] = useState(0);
  const [warehousesCurrentPage, setWarehousesCurrentPage] = useState(0);
  const rowsPerPage = 10;
  const [vendorsSearchQuery, setVendorsSearchQuery] = useState("");
  const [warehousesSearchQuery, setWarehousesSearchQuery] = useState("");
  const [approvers, setApprovers] = useState<Approver[]>([]);
  const [selectedApprovers, setSelectedApprovers] = useState<string[]>([]);
  const [labels, setLabels] = useState<LabelOption[]>([]);
  const [selectedLabelId, setSelectedLabelId] = useState<number | null>(null);
  const [orderDeadline, setOrderDeadline] = useState<string>("");
  const [orderType, setOrderType] = useState<"OPEN" | "CLOSED">("OPEN");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [modelTypes, setModelTypes] = useState<GetAllModelTypeArray[]>([]);
  const [selectedOEMTypeId, setSelectedOEMTypeId] = useState<number | null>(
    null
  );
  const [hasMoreParts, setHasMoreParts] = useState(true);
  const [hasMoreVendors, setHasMoreVendors] = useState(true);
  const [hasMoreWarehouses, setHasMoreWarehouses] = useState(true);
  const [isLoadingParts, setIsLoadingParts] = useState(false);
  const [remarks, setRemarks] = useState<string>("");
  const [selectedPartIds, setSelectedPartIds] = useState<Set<number>>(
    new Set()
  );
  const [isTypingVendors, setIsTypingVendors] = useState(false);
  const [isTypingWarehouses, setIsTypingWarehouses] = useState(false);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [isWarehouseInitialized, setIsWarehouseInitialized] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const calculateItemTotal = (
    quantity: number,
    price: number | string,
    tax: number
  ) => {
    const numericPrice =
      typeof price === "string" ? parseFloat(price) || 0 : price;
    const taxRate = tax / 100 || 0;
    return (quantity * numericPrice * (1 + taxRate)).toFixed(2);
  };
  const isFranchiseRole = isRoleDenied([
    "ROLE_ERP_FRANCHISE_ADMIN",
    "ROLE_FRANCHISE_INVENTORY_MANAGER",
  ]);

  const handleScroll = (
    event: React.SyntheticEvent,
    type: "parts" | "vendors" | "warehouses"
  ) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;

    // Add a small buffer to prevent premature triggering
    if (listboxNode.scrollHeight - position <= 5) {
      switch (type) {
        case "parts":
          if (hasMoreParts && !isLoadingParts) {
            setPartsCurrentPage((prev) => prev + 1);
          }
          break;
        case "vendors":
          if (hasMoreVendors) {
            setVendorsCurrentPage((prev) => prev + 1);
          }
          break;
        case "warehouses":
          if (hasMoreWarehouses) {
            setWarehousesCurrentPage((prev) => prev + 1);
          }
          break;
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    if (isSuccess) {
      onBack();
    }
  };

  // Initial data loading effect - only runs once when orderData is available
  // Initial data loading effect - only runs once when orderData is available
  useEffect(() => {
    if (orderData && !isInitialDataLoaded) {
      console.log("Loading order data:", orderData);
      console.log("Vendor details:", orderData.vendorDetails);

      setSelectedVendor(orderData.vendorDetails?.vendorName || "");
      setSelectedGSTTreatment(
        orderData.gstTreatment || "Registered Business - Regular"
      );

      const expectedArrivalDate = orderData.expectedArrival
        ? new Date(orderData.expectedArrival).toISOString().slice(0, 16)
        : "";
      setOrderDeadline(expectedArrivalDate);

      setOrderType(orderData.purchaseOrderType || "OPEN");
      setSelectedLabelId(orderData.labelId ?? orderData.label?.id ?? null);

      setRemarks(orderData.notes || orderData.note || "");

      if (
        orderData.purchaseOrderLines &&
        orderData.purchaseOrderLines.length > 0
      ) {
        const orderItems = orderData.purchaseOrderLines?.map((item: any) => ({
          description: item.part?.description || item.partName || "",
          referenceCode: item.part?.referenceCode || "",
          quantity: item.quantityOrdered?.toString() || "1",
          unit: "Units",
          price: item.unitPrice?.toString() || "0",
          tax: item.tax ?? 0,
          total: calculateItemTotal(
            item.quantityOrdered?.toString() || "1",
            item.unitPrice?.toString() || "0",
            item.tax ?? 0
          ),
          hsn: item.part?.hsn || "",
          partId: item.part?.id || undefined,
          id: item.part?.id || -1,
          externalId: 0,
          type: "",
          status: 0,
          costPrice: 0,
          averageCostPrice: 0,
          salesPrice: 0,
          penaltyType: "",
          deleted: false,
          updatedBy: 0,
          oemTypeId: item.part?.oemTypeId || undefined,
        }));

        setItems(orderItems);

        // Track selected parts
        const partIds = new Set<number>(
          orderItems
            ?.map((item: any) => (item.partId || item.id) as number)
            .filter((id: number) => id > 0)
        );
        setSelectedPartIds(partIds);

        // Set OEM type if available in order data
        if (orderData.oemTypeId) {
          setSelectedOEMTypeId(orderData.oemTypeId);
        }
      } else {
        setItems([
          {
            id: -1,
            externalId: 0,
            referenceCode: "",
            type: "",
            description: "",
            status: 0,
            costPrice: 0,
            averageCostPrice: 0,
            salesPrice: 0,
            tax: 0,
            price: 0,
            quantity: 0,
            penaltyType: "",
            deleted: false,
            updatedBy: 0,
            oemTypeId: undefined,
          },
        ]);
      }

      setIsInitialDataLoaded(true);

      setIsInitialDataLoaded(true);
    }
  }, [orderData, isInitialDataLoaded]);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const response = await LabelsApi.api.getLabels("PO_USAGE");
        setLabels(response.data.labels ?? []);
      } catch (error) {
        console.error("Error fetching purchase order labels", error);
      }
    };

    fetchLabels();
  }, []);

  // Separate effect to handle warehouse mapping when warehouses are loaded
  useEffect(() => {
    if (
      orderData &&
      isInitialDataLoaded &&
      warehouses.length > 0 &&
      !isWarehouseInitialized
    ) {
      const warehouseName =
        orderData.deliveryWareHouseDetails?.deliveryWarehouseName ||
        orderData.deliveryWarehouseDetails?.deliveryWarehouseName;

      if (warehouseName) {
        // Handle both single warehouse (string) and multiple warehouses (array)
        const warehouseNames = Array.isArray(warehouseName)
          ? warehouseName
          : [warehouseName];

        setSelectedWarehouses(
          warehouses.filter((w) => warehouseNames.includes(w.name))
        );
      }
      setIsWarehouseInitialized(true);
    }
  }, [orderData, isInitialDataLoaded, warehouses, isWarehouseInitialized]);

  useEffect(() => {
    if (
      orderData &&
      orderData.approvals &&
      Array.isArray(orderData.approvals) &&
      approvers.length > 0
    ) {
      const approvalNames = orderData.approvals?.map((a: any) =>
        a.name?.trim().toLowerCase()
      );
      const matchedApprovers = approvers.filter((a) =>
        approvalNames.includes(a.name?.trim().toLowerCase())
      );
      setSelectedApprovers(matchedApprovers?.map((a) => a.name));
    }
  }, [orderData, approvers]);

  const fetchModelTypes = useCallback(async () => {
    try {
      const response = await AllPartApi.api.getModelTypeAll();
      setModelTypes(response.data);
    } catch (error) {
      console.error("Error fetching model types", error);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const response = await GetAllVendorsListApi.api.getAllVendorsList(
        vendorsCurrentPage,
        rowsPerPage,
        vendorsSearchQuery
      );
      setVendors((prev) => {
        const newVendors = vendorsCurrentPage === 0
          ? response.data.data
          : [...prev, ...response.data.data];

        // If we have orderData with vendor details, ensure that vendor is in the list
        if (orderData?.vendorDetails && vendorsCurrentPage === 0) {
          const vendorExists = newVendors.some(
            v => v.id === orderData.vendorDetails.vendorId
          );
          if (!vendorExists) {
            // Add the vendor from orderData if it's not in the list
            return [{
              id: orderData.vendorDetails.vendorId,
              name: orderData.vendorDetails.vendorName,
              gst: orderData.gstTreatment || "",
              // Add other required vendor properties with defaults
            } as any, ...newVendors];
          }
        }

        return newVendors;
      });
      setHasMoreVendors(response.data.data.length === rowsPerPage);
    } catch (error) {
      console.error("Error fetching vendors", error);
    }
  }, [vendorsCurrentPage, vendorsSearchQuery, orderData]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
        warehousesCurrentPage,
        rowsPerPage,
        warehousesSearchQuery
      );
      setWarehouses((prev) => {
        const newWarehouses = warehousesCurrentPage === 0
          ? response.data.data
          : [...prev, ...response.data.data];

        // If we have orderData with warehouse details, ensure that warehouse is in the list
        if (orderData?.deliveryWareHouseDetails && warehousesCurrentPage === 0) {
          const warehouseId = orderData.deliveryWareHouseDetails.deliveryWarehouseId;
          const warehouseName = orderData.deliveryWareHouseDetails.deliveryWarehouseName;

          if (warehouseId && warehouseName) {
            const warehouseExists = newWarehouses.some(w => w.id === warehouseId);
            if (!warehouseExists) {
              // Add the warehouse from orderData if it's not in the list
              return [{
                id: warehouseId,
                name: warehouseName,
                code: orderData.deliveryWareHouseDetails.deliveryWarehouseCode || "",
                // Add other required warehouse properties with defaults
              } as any, ...newWarehouses];
            }
          }
        }

        return newWarehouses;
      });
      setHasMoreWarehouses(response.data.data.length === rowsPerPage);
    } catch (error) {
      console.error("Error fetching warehouses", error);
    }
  }, [warehousesCurrentPage, warehousesSearchQuery]);

  const fetchApprovers = useCallback(async () => {
    try {
      const response = await GetAllApproverListApi.api.getAllApproverList();
      setApprovers(response.data);
    } catch (error) {
      console.error("Error fetching approvers", error);
    }
  }, []);

  const fetchPaginatedPartsBySearchTerm = useCallback(
    (
      currentPageNumber: number,
      currentSearchQueryTerm: string,
      itemIndex?: number
    ): Promise<{
      options: OptionType<TransferOrderPartDetails>[];
      hasMore: boolean;
    }> => {
      const oemTypeIdToUse =
        itemIndex !== undefined && items[itemIndex]?.oemTypeId
          ? items[itemIndex].oemTypeId
          : selectedOEMTypeId;

      const fetchMultiplier = Math.max(
        2,
        Math.ceil(selectedPartIds.size / rowsPerPage) + 1
      );
      const adjustedPageSize = rowsPerPage * fetchMultiplier;

      return AllPartApi.api
        .getPartList(
          currentPageNumber,
          adjustedPageSize,
          currentSearchQueryTerm,
          undefined,
          undefined,
          oemTypeIdToUse || undefined
        )
        .then(({ data: response }) => {
          const availableParts = response.data.filter(
            (part) => !selectedPartIds.has(part.id)
          );

          return {
            options: availableParts?.map((part) => ({
              value: part.id.toString(),
              label: part.description,
              data: part,
            })),
            hasMore:
              currentPageNumber < response.totalPages - 1 ||
              availableParts.length >= rowsPerPage,
          };
        })
        .catch(() => ({ options: [], hasMore: false }));
    },
    [selectedOEMTypeId, selectedPartIds, items]
  );

  const handlePartSelect =
    (index: number) =>
      (event: any, value: OptionType<TransferOrderPartDetails> | null) => {
        const newItems = [...items];
        const oldPartId = newItems[index].id;

        if (value) {
          if (value.data) {
            const partDetails = value.data;

            // Remove old part ID from selected parts (if it exists and is valid)
            if (oldPartId > 0) {
              setSelectedPartIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(oldPartId);
                return newSet;
              });
            }

            setSelectedPartIds((prev) => new Set(prev).add(partDetails.id));

            newItems[index] = {
              ...newItems[index],
              description: partDetails.description || "",
              referenceCode: partDetails.referenceCode || "",
              id: partDetails.id,
              price: partDetails.costPrice || 0,
              tax: partDetails.tax ?? 0,
              quantity: newItems[index].quantity || 1, // Set default quantity to 1 if not set
              oemTypeId: selectedOEMTypeId || undefined,
            };

            setItems(newItems);
          }
        } else {
          // Remove old part ID from selected parts when deselecting
          if (oldPartId > 0) {
            setSelectedPartIds((prev) => {
              const newSet = new Set(prev);
              newSet.delete(oldPartId);
              return newSet;
            });
          }

          // Reset the item when a part is deselected
          newItems[index] = {
            id: -1,
            externalId: 0,
            referenceCode: "",
            type: "",
            description: "",
            status: 0,
            costPrice: 0,
            averageCostPrice: 0,
            salesPrice: 0,
            tax: 0,
            price: 0,
            quantity: newItems[index].quantity || 0, // Preserve quantity
            penaltyType: "",
            deleted: false,
            updatedBy: 0,
            oemTypeId: undefined,
          };

          setItems(newItems);
        }
      };
  const handleTaxChange = (index: number, value: string) => {
    const newItems = [...items];
    // Allow only numbers and decimal point
    const numericValue = value.replace(/[^\d.]/g, "");
    const tax = parseFloat(numericValue);

    newItems[index] = {
      ...newItems[index],
      tax: isNaN(tax) ? 0 : tax,
    };

    setItems(newItems);
  };
  const handleOEMTypeChange = (newValue: GetAllModelTypeArray | null) => {
    const newOEMTypeId = newValue ? newValue.id : null;
    setSelectedOEMTypeId(newOEMTypeId);

    // DO NOT reset items or selected parts when OEM type changes
    // This allows parts from different OEM types to coexist
  };

  useEffect(() => {
    fetchModelTypes();
    fetchApprovers();
  }, [fetchModelTypes, fetchApprovers]);

  useEffect(() => {
    const debouncedFetchVendors = debounce(fetchVendors, 500);
    debouncedFetchVendors();
    return () => debouncedFetchVendors.cancel();
  }, [fetchVendors, vendorsCurrentPage]);

  useEffect(() => {
    const debouncedFetchWarehouses = debounce(fetchWarehouses, 500);
    debouncedFetchWarehouses();
    return () => debouncedFetchWarehouses.cancel();
  }, [fetchWarehouses, warehousesCurrentPage]);

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        id: -1,
        externalId: 0,
        referenceCode: "",
        type: "",
        description: "",
        status: 0,
        costPrice: 0,
        averageCostPrice: 0,
        salesPrice: 0,
        tax: 0,
        price: 0,
        quantity: 0,
        penaltyType: "",
        deleted: false,
        updatedBy: 0,
        oemTypeId: selectedOEMTypeId || undefined, // Set the current OEM type for new item
      },
    ]);
  };

  const handleDeleteItem = (index: number) => {
    if (items.length <= 1) {
      setModalMessage("At least one item is required");
      setModalOpen(true);
      return;
    }

    // Remove the part ID from selected parts when deleting
    const itemToDelete = items[index];
    if (itemToDelete.id > 0) {
      setSelectedPartIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemToDelete.id);
        return newSet;
      });
    }

    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const validateForm = (): boolean => {
    if (!selectedVendor) {
      setModalMessage("Please select a vendor");
      setModalOpen(true);
      return false;
    }

    if (!selectedWarehouses.length) {
      setModalMessage("Please select at least one warehouse");
      setModalOpen(true);
      return false;
    }

    if (!orderDeadline) {
      setModalMessage("Please set an order deadline");
      setModalOpen(true);
      return false;
    }

    if (selectedLabelId === null) {
      setModalMessage("Please select a PO usage label");
      setModalOpen(true);
      return false;
    }

    if (items.some((item) => !item.description || !item.quantity)) {
      setModalMessage("Please fill all item fields");
      setModalOpen(true);
      return false;
    }

    return true;
  };

  const handleCreateOrder = async (statusType: "CREATED" | "DRAFT") => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const vendor = vendors.find((v) => v.name === selectedVendor);
      const selectedWarehouseObjs = selectedWarehouses;
      if (!vendor || !selectedWarehouseObjs.length) {
        setModalMessage("Vendor or at least one Warehouse not selected");
        setModalOpen(true);
        return;
      }

      const orderBody: PurchaseOrderBody = {
        vendorId: vendor.id,
        deliveryWarehouseId: selectedWarehouseObjs?.map((w) => w.id),
        confirmationDate: new Date().toISOString(),
        expectedArrival: orderDeadline,
        gst: selectedGSTTreatment || null,
        purchaseOrderType: orderType,
        statusType,
        labelId: selectedLabelId ?? undefined,
        purchaseOrderLineRequestList: items?.map((item) => ({
          partId: item.id || 0,
          quantityOrdered: item.quantity || 0,
          price: item.price || 0,
          tax: item.tax ?? 0,
        })),
        requiredApprovals: approvers
          .filter((approver) => selectedApprovers.includes(approver.name))
          ?.map((approver) => approver.userId),
        note: remarks,
        // oemTypeId: selectedOEMTypeId || undefined,
      };

      let response;
      if (orderData?.id) {
        response = await ProductOrderApi.api.updatePurchaseOrder(
          orderData.id,
          orderBody
        );
      } else {
        response = await ProductOrderApi.api.createPurchaseOrder(orderBody);
      }

      // Handle API response messages
      if (response.data && response.message) {
        setModalMessage(response.message);
      } else {
        setModalMessage(
          `Purchase order ${statusType === "DRAFT"
            ? orderData
              ? "updated as draft"
              : "created as draft"
            : orderData
              ? "updated successfully"
              : "created successfully"
          }`
        );
      }

      setIsSuccess(true);
      setModalOpen(true);
    } catch (error: any) {
      console.error("Error creating purchase order", error);
      let errorMessage = "Error creating purchase order";
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      }
      setModalMessage(errorMessage);
      setIsSuccess(false);
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!orderData?.id) return;

    setLoading(true);
    try {
      const response = await ProductOrderApi.api.cancelPurchaseOrder(
        orderData.id
      );
      setModalMessage("Purchase order cancelled successfully");
      setIsSuccess(true);
      setModalOpen(true);
    } catch (error: any) {
      console.error("Error cancelling purchase order", error);
      let errorMessage = "Error cancelling purchase order";
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      }
      setModalMessage(errorMessage);
      setIsSuccess(false);
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    let totalAmount = 0;
    let totalGST = 0;

    items.forEach((item) => {
      const quantity = item.quantity || 0;
      const price =
        typeof item.price === "string"
          ? parseFloat(item.price) || 0
          : item.price || 0;
      const taxRate = item.tax / 100 || 0;

      const itemTotal = quantity * price;
      const itemGST = itemTotal * taxRate;

      totalAmount += itemTotal;
      totalGST += itemGST;
    });

    return {
      totalAmount: totalAmount.toFixed(2),
      totalGST: totalGST.toFixed(2),
      overallTotal: (totalAmount + totalGST).toFixed(2),
    };
  };

  const { totalAmount, totalGST, overallTotal } = calculateTotals();

  const canClosePO = () => {
    return (
      orderData &&
      (orderData.status === "CREATED" || orderData.status === "APPROVED")
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="w-full p-4">
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: "600",
              color: "#1E1E1E",
              textAlign: "left",
              fontSize: "1.5rem",
            }}>
            {orderData ? "Edit" : "Create"} Purchase Order
          </Typography>

          <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
            <Link
              to="/home"
              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
              Dashboard
            </Link>
            <span className="text-sm text-gray-400">/</span>
            <Link
              to={`/home${statusFilter ? `?status=${statusFilter}` : ""}`}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
              Orders
            </Link>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">
              {orderData ? "Edit" : "Create"} Order
            </span>
          </div>
        </Box>

        <Paper
          sx={{
            p: 4,
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            backgroundColor: "white",
            maxWidth: "1600px",
            margin: "0 auto",
          }}>
          {/* Form Section */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Vendor
                  </label>
                  <Autocomplete
                    fullWidth
                    options={vendors}
                    getOptionLabel={(option) => option.name}
                    value={
                      vendors.find(
                        (vendor) => vendor.name === selectedVendor
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      setSelectedVendor(newValue ? newValue.name : "");
                      setSelectedGSTTreatment(newValue?.gst ?? "");
                    }}
                    onInputChange={(event, newInputValue) => {
                      setIsTypingVendors(true);
                      setVendorsSearchQuery(newInputValue);
                      // Only reset page when search query actually changes, not on every input
                      if (newInputValue !== vendorsSearchQuery) {
                        setVendorsCurrentPage(0);
                      }
                    }}
                    onOpen={() => setIsTypingVendors(false)}
                    onClose={() => setIsTypingVendors(false)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        placeholder="Select vendor"
                        InputProps={{
                          ...params.InputProps,
                          style: { fontSize: "0.875rem" },
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (e) => handleScroll(e, "vendors"),
                      style: { maxHeight: 200, overflow: "auto" },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    GST Treatment
                  </label>
                  <TextField
                    fullWidth
                    value={selectedGSTTreatment}
                    disabled
                    variant="standard"
                    InputProps={{
                      readOnly: true,
                      style: { fontSize: "0.875rem" },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Order Deadline
                  </label>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    value={orderDeadline}
                    onChange={(e) => setOrderDeadline(e.target.value)}
                    variant="standard"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      style: { fontSize: "0.875rem" },
                    }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Deliver To
                  </label>
                  <Autocomplete
                    fullWidth
                    multiple={!isEditMode}
                    options={warehouses}
                    getOptionLabel={(option) => option.name || ""}
                    value={isEditMode ? (selectedWarehouses[0] || null) : selectedWarehouses}
                    onChange={(event, newValue) => {
                      if (isEditMode) {
                        // In edit mode, handle single selection (newValue will be null when cleared)
                        setSelectedWarehouses(newValue ? [newValue as GetAllWarehouseApiResponse["data"]["data"][0]] : []);
                      } else {
                        // In create mode, handle multiple selection
                        setSelectedWarehouses((newValue as GetAllWarehouseApiResponse["data"]["data"]) || []);
                      }
                    }}
                    {...(!isEditMode && {
                      inputValue: warehousesSearchQuery,
                      onInputChange: (event, newInputValue) => {
                        setIsTypingWarehouses(true);
                        setWarehousesSearchQuery(newInputValue || "");
                        // Only reset page when search query actually changes, not on every input
                        if (newInputValue !== warehousesSearchQuery) {
                          setWarehousesCurrentPage(0);
                        }
                      }
                    })}
                    onOpen={() => setIsTypingWarehouses(false)}
                    onClose={() => setIsTypingWarehouses(false)}
                    filterOptions={(options, { inputValue }) => {
                      const filtered = options.filter((option) =>
                        option.name
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      );
                      return filtered;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        placeholder={isEditMode ? "Select warehouse" : "Select warehouse(s)"}
                        InputProps={{
                          ...params.InputProps,
                          style: { fontSize: "0.875rem" },
                        }}
                      />
                    )}
                    ListboxProps={{
                      onScroll: (e) => handleScroll(e, "warehouses"),
                      style: { maxHeight: 200, overflow: "auto" },
                    }}
                  />
                </Box>
              </Grid>
              <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER", "ROLE_ERP_FRANCHISE_ADMIN"]}>
                <Grid item xs={12} md={4}>
                  <Box>
                    <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                      Approval By
                    </label>
                    <Autocomplete
                      fullWidth
                      multiple
                      options={approvers}
                      getOptionLabel={(option) => option.name}
                      value={approvers.filter((approver) =>
                        selectedApprovers
                          ?.map((n) => n.trim().toLowerCase())
                          .includes(approver.name.trim().toLowerCase())
                      )}
                      onChange={(event, newValue) => {
                        setSelectedApprovers(
                          newValue?.map((approver) => approver.name)
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="Select approvers"
                          InputProps={{
                            ...params.InputProps,
                            style: { fontSize: "0.875rem" },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Grid>
              </RoleGuard>
              <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER", "ROLE_ERP_FRANCHISE_ADMIN"]}>
                <Grid item xs={12} md={4}>
                  <Box>
                    <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                      OEM Type (for filtering parts)
                    </label>
                    <Autocomplete
                      fullWidth
                      options={modelTypes}
                      getOptionLabel={(option) => option.description}
                      value={
                        modelTypes.find(
                          (modelType) => modelType.id === selectedOEMTypeId
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        handleOEMTypeChange(newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="Select OEM type"
                          InputProps={{
                            ...params.InputProps,
                            style: { fontSize: "0.875rem" },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Grid>
              </RoleGuard>
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    PO Usage Label <span className="text-red-500">*</span>
                  </label>
                  <Autocomplete
                    fullWidth
                    options={labels}
                    getOptionLabel={(option) => option.label}
                    value={
                      labels.find((label) => label.id === selectedLabelId) ||
                      null
                    }
                    onChange={(event, newValue) =>
                      setSelectedLabelId(newValue?.id ?? null)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        placeholder="Select PO usage"
                        required
                        InputProps={{
                          ...params.InputProps,
                          style: { fontSize: "0.875rem" },
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Items Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: "left",
                fontWeight: "600",
                color: "#1E1E1E",
                mb: 2,
                fontSize: "1.125rem",
              }}>
              Items
            </Typography>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 1400 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "50px",
                      }}>
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "300px",
                      }}>
                      Product Description
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "150px",
                      }}>
                      Reference Code
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "120px",
                      }}>
                      HSN
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "100px",
                      }}>
                      Quantity
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "130px",
                      }}>
                      Unit Of Measure
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "150px",
                      }}>
                      Unit Price
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "100px",
                      }}>
                      Tax %
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "150px",
                      }}>
                      Total
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        minWidth: "80px",
                      }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontSize: "0.875rem" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ minWidth: "300px" }}>
                        <AppAutoCompleteWithSearchAPI
                          key={`part-select-${index}-oem-${item.oemTypeId || selectedOEMTypeId || "none"
                            }`}
                          onChange={handlePartSelect(index)}
                          initialPageNumber={0}
                          value={
                            item.id >= 0
                              ? {
                                value: item.id?.toString() || "",
                                label: item.description,
                                data: item,
                              }
                              : null
                          }
                          getData={function (data: {
                            currentPageNumber: number;
                            currentSearchQueryTerm: string;
                          }): Promise<{
                            options: OptionType<TransferOrderPartDetails>[];
                            hasMore: boolean;
                          }> {
                            return fetchPaginatedPartsBySearchTerm(
                              data.currentPageNumber,
                              data.currentSearchQueryTerm,
                              index
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          value={item.referenceCode}
                          variant="standard"
                          disabled
                          InputProps={{
                            style: { fontSize: "0.875rem" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          value={item.hsn || ""}
                          variant="standard"
                          disabled
                          InputProps={{
                            style: { fontSize: "0.875rem" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          value={item.quantity || ""}
                          onChange={({ target: { value = "0" } }) => {
                            const newItems = [...items];
                            newItems[index].quantity = Math.max(0, +value || 0);
                            setItems(newItems);
                          }}
                          onBlur={() => {
                            if (!item.quantity) {
                              const newItems = [...items];
                              newItems[index].quantity = 1;
                              setItems(newItems);
                            }
                          }}
                          variant="standard"
                          InputProps={{
                            style: { fontSize: "0.875rem" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          value={"Units"}
                          variant="standard"
                          disabled
                          InputProps={{
                            style: { fontSize: "0.875rem" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          value={item.price ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newItems = [...items];

                            // Allow empty string or 0
                            if (value === "" || value === "0") {
                              newItems[index].price = 0;
                              setItems(newItems);
                              return;
                            }

                            // Allow digits, single decimal point, and up to 2 decimal places
                            // Including case where value starts with 0
                            if (/^[0-9]*\.?[0-9]{0,2}$/.test(value)) {
                              newItems[index].price =
                                value === "" ? 0 : parseFloat(value);
                              setItems(newItems);
                            }
                          }}
                          onBlur={(e) => {
                            const newItems = [...items];
                            const value = e.target.value;
                            // Convert to number, preserve 0
                            newItems[index].price =
                              value === "" ? 0 : parseFloat(value);
                            setItems(newItems);
                          }}
                          variant="standard"
                          disabled={isFranchiseRole}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                ₹
                              </InputAdornment>
                            ),
                            style: { fontSize: "0.875rem" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={item.tax}
                          onChange={(e) =>
                            handleTaxChange(index, e.target.value)
                          }
                          disabled={item.id === -1}
                          variant="standard"
                          InputProps={{
                            style: { fontSize: "0.875rem" },
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          value={calculateItemTotal(
                            item.quantity,
                            item.price,
                            item.tax
                          )}
                          variant="standard"
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                ₹
                              </InputAdornment>
                            ),
                            style: { fontSize: "0.875rem" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleDeleteItem(index)}
                          sx={{ color: "#FF0000" }}
                          size="small"
                          disabled={items.length <= 1}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <IconButton
                onClick={handleAddItem}
                sx={{ color: "#1976D2", mr: 1 }}
                size="small">
                <AddIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body1"
                sx={{
                  cursor: "pointer",
                  color: "#1976D2",
                  fontSize: "0.875rem",
                }}
                onClick={handleAddItem}>
                Add Item
              </Typography>
            </Box>
          </Box>

          {/* Remarks Section */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box>
                  <label className="block text-black-600 text-sm font-medium text-start mb-1">
                    Remarks
                  </label>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    variant="outlined"
                    placeholder="Enter any additional notes or remarks..."
                    InputProps={{
                      style: {
                        fontSize: "0.875rem",
                        borderRadius: "8px",
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Totals and Actions Section */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ mb: 2, textAlign: "right" }}>
              <Typography
                variant="body1"
                sx={{ color: "#646464", fontSize: "0.875rem" }}>
                Amount - ₹ {totalAmount}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#646464", fontSize: "0.875rem" }}>
                IGST - ₹ {totalGST}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#1E1E1E",
                  fontWeight: "600",
                  fontSize: "0.875rem",
                }}>
                Total - ₹ {overallTotal}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              {orderData?.id && (
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoryDialogOpen(true)}
                  sx={{
                    padding: "8px 20px",
                    borderRadius: "32px",
                    textTransform: "none",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                  }}>
                  History
                </Button>
              )}
              {orderData ? (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleCancelOrder}
                  sx={{
                    padding: "8px 20px",
                    borderRadius: "32px",
                    backgroundColor: "#FF0000",
                    textTransform: "none",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                  }}>
                  Cancel Order
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  onClick={onBack}
                  sx={{
                    padding: "8px 20px",
                    borderRadius: "32px",
                    textTransform: "none",
                    fontWeight: "500",
                    fontSize: "0.875rem",
                    backgroundColor: "#F94932",
                  }}>
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleCreateOrder("DRAFT")}
                sx={{
                  padding: "8px 20px",
                  borderRadius: "32px",
                  backgroundColor: "#1366D9",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                }}>
                {orderData ? "Update" : "Create"} And Draft
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleCreateOrder("CREATED")}
                sx={{
                  padding: "8px 20px",
                  borderRadius: "32px",
                  backgroundColor: "#1E1E1E",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                }}>
                {orderData ? "Update" : "Create"}
                {!isFranchiseRole && " And Send for Approval"}
              </Button>
            </Box>

            {orderData?.id && (
              <PurchaseOrderHistory
                purchaseOrderId={orderData.id}
                variant="dialog"
                open={historyDialogOpen}
                onClose={() => setHistoryDialogOpen(false)}
              />
            )}
          </Box>

          {/* Modal */}
          <Modal
            open={modalOpen}
            onClose={handleCloseModal}
            aria-labelledby="modal-title"
            aria-describedby="modal-description">
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 300,
                bgcolor: "background.paper",
                border: "2px solid #000",
                boxShadow: 24,
                p: 4,
                textAlign: "center",
                borderRadius: "8px",
                backgroundColor: isSuccess ? "#d4edda" : "#f8d7da",
                color: isSuccess ? "#155724" : "#721c24",
              }}>
              <Typography
                id="modal-title"
                variant="h6"
                component="h2"
                sx={{ fontSize: "1rem", mb: 2 }}>
                {modalMessage}
              </Typography>
              <Button onClick={handleCloseModal} sx={{ fontSize: "0.875rem" }}>
                Close
              </Button>
            </Box>
          </Modal>
        </Paper>
      </div>
    </div>
  );
};

export default CreatePurchaseOrder;
